/**
 * The one HTTP transport for the platform's management API, plus the request/
 * response types generated from its OpenAPI document (`src/schema.d.ts`,
 * regenerated with `pnpm --filter @tes/api-client generate` against a running
 * stack). The admin SPA builds its `api` object on this; the public signing
 * page deliberately keeps its own tiny non-throwing wrapper.
 */
export type { paths, components, operations } from './schema';

export interface ApiClientOptions {
  /** Base URL prefix, e.g. "" (same-origin via Caddy) or "http://localhost:3000". */
  baseUrl?: string;
  /** Static bearer token (API key). Ignored when `getToken` is given. */
  token?: string;
  /** Resolved per request — the admin session JWT lives in storage and can change. */
  getToken?: () => string | null | undefined;
  /**
   * Called when an authenticated request comes back 401 (session expired or
   * revoked). The request still rejects with an ApiError afterwards.
   */
  onUnauthorized?: () => void;
  fetch?: typeof fetch;
}

export interface RequestOptions {
  /** `false` to send no Authorization header (login). Default true. */
  auth?: boolean;
}

/** Non-2xx response. `message` is the server's message (arrays joined) when it sent one. */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly detail: unknown,
  ) {
    super(extractMessage(detail, status));
    this.name = 'ApiError';
  }
}

function extractMessage(detail: unknown, status: number): string {
  if (detail && typeof detail === 'object') {
    const msg = (detail as { message?: unknown }).message;
    if (Array.isArray(msg)) return msg.join(', ');
    if (typeof msg === 'string' && msg) return msg;
  }
  return `Request failed (${status})`;
}

/** `/health` is not described in the OpenAPI document (it lives outside /v1). */
export interface HealthResponse {
  status: 'ok' | 'degraded';
  checks: Record<string, 'up' | 'down'>;
  timestamp: string;
}

export class ApiClient {
  private readonly baseUrl: string;
  private token?: string;
  private readonly getToken?: () => string | null | undefined;
  private readonly onUnauthorized?: () => void;
  private readonly fetchImpl: typeof fetch;

  constructor(opts: ApiClientOptions = {}) {
    this.baseUrl = (opts.baseUrl ?? '').replace(/\/$/, '');
    this.token = opts.token;
    this.getToken = opts.getToken;
    this.onUnauthorized = opts.onUnauthorized;
    this.fetchImpl = opts.fetch ?? globalThis.fetch.bind(globalThis);
  }

  setToken(token?: string): void {
    this.token = token;
  }

  private currentToken(): string | undefined {
    return (this.getToken ? this.getToken() : this.token) ?? undefined;
  }

  async request<T>(path: string, init: RequestInit = {}, opts: RequestOptions = {}): Promise<T> {
    const headers = new Headers(init.headers);
    const useAuth = opts.auth !== false;
    const token = useAuth ? this.currentToken() : undefined;
    if (token) headers.set('Authorization', `Bearer ${token}`);
    const isForm = init.body instanceof FormData;
    if (!isForm && init.body && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }

    const res = await this.fetchImpl(`${this.baseUrl}${path}`, { ...init, headers });

    if (res.status === 401 && token) {
      this.onUnauthorized?.();
      throw new ApiError(401, { message: 'Session expired' });
    }
    if (!res.ok) {
      let detail: unknown;
      try {
        detail = await res.json();
      } catch {
        detail = await res.text().catch(() => undefined);
      }
      throw new ApiError(res.status, detail);
    }
    if (res.status === 204) return undefined as T;
    const contentType = res.headers.get('Content-Type') ?? '';
    return (contentType.includes('application/json') ? await res.json() : await res.text()) as T;
  }

  health(): Promise<HealthResponse> {
    return this.request<HealthResponse>('/health');
  }
}

export function createApiClient(opts: ApiClientOptions = {}): ApiClient {
  return new ApiClient(opts);
}
